/**
 * 路由守卫。
 *
 * **前端拦截只是不给入口，不是安全边界**（spec 00 §3.2-9）：
 * 绕过它拿不到任何数据，服务端会独立做角色 + 资源归属两道校验。
 * 这里的价值是不让用户点进一个必然 403 的页面。
 */
import { Suspense, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Spin } from 'antd';
import { useAuth } from '@/stores/auth';
import { can, type Capability } from '@/lib/permission';
import { CONSOLE_HOME } from '@/lib/paths';
import { EmptyHint } from '@/components/data/EmptyHint';

export function Loading() {
  return (
    <div className="app-fallback">
      <Spin />
    </div>
  );
}

export function Guard({ cap, children }: { cap?: Capability; children: ReactNode }) {
  const me = useAuth((s) => s.me);
  const bootstrapping = useAuth((s) => s.bootstrapping);
  const location = useLocation();

  if (bootstrapping) return <Loading />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (cap && !can(me.roles, cap)) {
    // 连今天都看不了时再送回今天只会原地打转，就地给空态（顶栏还能切企业、退出）
    if (location.pathname === CONSOLE_HOME) {
      return (
        <EmptyHint
          page
          title="这家企业里你还没有能看的页面"
          desc="请企业主或管理员给你开通权限；也可以在右上角切换到别的企业。"
        />
      );
    }
    return <Navigate to={CONSOLE_HOME} replace />;
  }

  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}
